import { ThemeContext } from '../../context/theme.context';
import {
  MultiSelectComponent,
  CheckBoxSelection,
  Inject,
} from '@syncfusion/ej2-react-dropdowns';
import { getEvents } from '../../services/events/event';
import { getPlan } from '../../services/ControlCenter/PlanServices';
import TheTabComponent from './TheTabComponent';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeResetDataLoadingBoolenValue,
  setIsAppliedEventPlan,
  setIsPlanSelected,
} from '../../redux/search.slice';
import { FcCancel } from 'react-icons/fc';
import GlobalDialog from '../../components/GlobalDialog';
import { ButtonPropsModel } from '@syncfusion/ej2-react-popups';
import Message from '../../utils/Message/Message';
import { SyncFullOperations } from '../../services/MexServices/Orders/OrderService';
import { toast } from 'react-toastify';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';

interface IProps {}

let thePlanData: Array<any> = [];
const CloudPlanner: React.FC<IProps> = () => {
  const context = useContext(ThemeContext);
  const dispatch = useDispatch();

  const isResetDataLoadingFromRedux = useSelector(
    (state: any) => state.search.isResetDataLoading
  );

  const [eventsData, setEventsData] = useState<Array<any>>([]);
  const [selectedEventIds, setSelectedEventIds] = useState<number[]>([]);
  const [selectedPlanIds, setSelectedPlanIds] = useState<number[]>([]);
  const [allPlansData, setAllPlansData] = useState<Array<any>>([]);
  const allPlansDataRef = useRef<Array<any>>([]);
  const selectedEventIdsRef = useRef<number[]>([]);
  const selectedPlanIdsRef = useRef<number[]>([]);

  const [ResetData, setResetData] = useState<boolean>(false);
  const [isResetDataLoading, setisResetDataLoading] = useState<boolean>(false);
  const [resetDataToastinId, setresetDataToastinId]: any = useState(null);
  const [isDataResettingComponentHandle, setisDataResettingComponentHandle] =
    useState<boolean>(false);
  const mountedRef = useRef(true);

  const updateAllPlansData = (plans: Array<any>) => {
    allPlansDataRef.current = plans;
    setAllPlansData(plans);
  };

  const updateSelectedEventIds = (ids: number[]) => {
    selectedEventIdsRef.current = ids;
    setSelectedEventIds(ids);
  };

  const planMultiSelectRef = useRef<MultiSelectComponent>(null);

  const updateSelectedPlanIds = (ids: number[]) => {
    selectedPlanIdsRef.current = ids;
    setSelectedPlanIds([...ids]);
  };

  useEffect(() => {
    mountedRef.current = true;

    const savedEventIds: number[] = JSON.parse(
      localStorage.getItem('cs_event_dd_values') || '[]'
    );
    const savedPlanIds: number[] = JSON.parse(
      localStorage.getItem('cs_plan_dd_values') || '[]'
    );

    const fetchData = async () => {
      const result = await getEvents();
      const incomingEvents = Array.isArray(result?.data) ? result.data : [];
      if (mountedRef.current) {
        setEventsData(incomingEvents);
      }

      const allPlans = await getPlan();
      const incomingPlans = Array.isArray(allPlans?.data) ? allPlans.data : [];
      if (mountedRef.current) {
        updateAllPlansData(incomingPlans);
        
        if (savedEventIds.length > 0) {
          updateSelectedEventIds(savedEventIds);
          updateSelectedPlanIds(savedPlanIds);
          dispatchPlanSelection(savedPlanIds);
        } else {
          dispatchPlanSelection([]);
        }
      }
    };

    fetchData();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setisDataResettingComponentHandle(isResetDataLoadingFromRedux);
    if (isDataResettingComponentHandle) {
      toast.update(resetDataToastinId, {
        render: 'Data Successfully Resetted',
        type: 'success',
        autoClose: 4000,
        isLoading: false,
      });
    }
  }, [isResetDataLoadingFromRedux]);

  const fetchPlansForEvent = async (eventIds: string[]): Promise<Array<any>> => {
    try {
      const query = eventIds.length > 0 ? eventIds.join(',') : '';
      const res: any = await getPlan(query);
      return Array.isArray(res?.data) ? res.data : [];
    } catch {
      return [];
    }
  };

  const fetchEvents = async () => {
    try {
      const result = await getEvents();
      const incomingData = Array.isArray(result?.data) ? result.data : [];
      if (mountedRef.current) {
        setEventsData(incomingData);
      }
      return incomingData;
    } catch { 
      return [];
    }
  };

  const dispatchPlanSelection = (planIds: number[]) => {
    const primaryPlanId = planIds.length > 0 ? planIds[0] : 0;
      dispatch(
        setIsPlanSelected({
          isOrderPlanSelected: true,
          isOpPlanSelected: true,
          planId: primaryPlanId,
          planIds: planIds,
        })
      );
  };

  const handleEventChange = useCallback((args: any) => {
    const newSelectedEventIds: number[] = [...(args.value ?? [])];
    updateSelectedEventIds(newSelectedEventIds);
    localStorage.setItem('cs_event_dd_values', JSON.stringify(newSelectedEventIds));

    if (newSelectedEventIds.length === 0) {
      updateSelectedPlanIds([]);
      localStorage.setItem('cs_plan_dd_values', '[]');
      dispatchPlanSelection([]);
      return;
    }

    const currentSelectedPlans = selectedPlanIdsRef.current;
    const numSelectedEventIds = newSelectedEventIds.map(Number);
    
    const cleanedPlanIds = currentSelectedPlans.filter((id) => {
      const plan = allPlansDataRef.current.find((p) => p.ID === id);
      if (!plan) return false;
      const pEventId = plan.event_id || plan.eventId || plan.EventId;
      return numSelectedEventIds.includes(Number(pEventId));
    });

    if (cleanedPlanIds.length !== currentSelectedPlans.length) {
      updateSelectedPlanIds(cleanedPlanIds);
      localStorage.setItem('cs_plan_dd_values', JSON.stringify(cleanedPlanIds));
      dispatchPlanSelection(cleanedPlanIds);
    }
  }, []);

  const handlePlanChange = useCallback((args: any) => {
    const newSelectedPlanIds: number[] = args.value ?? [];
    updateSelectedPlanIds(newSelectedPlanIds);
    localStorage.setItem('cs_plan_dd_values', JSON.stringify(newSelectedPlanIds));
    dispatchPlanSelection(newSelectedPlanIds);
  }, []);

  const allAvailablePlans = useMemo(() => {
    if (selectedEventIds.length === 0) {
      return allPlansData;
    }
    const numSelectedEventIds = selectedEventIds.map(Number);
    return allPlansData.filter((p) => {
      const pEventId = p.event_id || p.eventId || p.EventId;
      return numSelectedEventIds.includes(Number(pEventId));
    });
  }, [allPlansData, selectedEventIds]);

  const resetDataFunction = () => {
    setisResetDataLoading(true);
    Message.success('Data is resetting...');
    dispatch(changeResetDataLoadingBoolenValue());
    setResetData(false);
    SyncFullOperations()
      .then((result) => {
        if (result.status === 200) {
          toast.update(resetDataToastinId, {
            render: 'All is good',
            type: 'success',
            isLoading: false,
          });
          setisResetDataLoading(false);
          Message.success('Synced successfully...');
          dispatch(changeResetDataLoadingBoolenValue());
        }
      })
      .catch(() => {
        Message.error('Sync failed!!!');
      });
  };

  const resetDataButton: ButtonPropsModel[] = [
    {
      buttonModel: { content: 'Yes' },
      click: () => {
        setResetData(false);
        resetDataFunction();
      },
    },
    {
      buttonModel: { content: 'No' },
      click: () => setResetData(false),
    },
  ];

  return (
    <div>
      <div
        className='header-cloud-scheduler-fixed'
        style={{
          marginRight: 30,
          marginTop: 30,
        }}
      >
        {ResetData && (
          <GlobalDialog
            showDialog={ResetData}
            setShowDialog={(val) => setResetData(val)}
            header='Reset data Message'
            width={'20%'}
            height={'20%'}
            hideDoneButton={true}
            content={'Are you sure to reset data ?'}
            buttons={resetDataButton}
          />
        )}

        <div className='cloud-scheduler-input'>
          <MultiSelectComponent
            placeholder='Event'
            dataSource={eventsData}
            fields={{ text: 'eventCode', value: 'eventId' }}
            value={selectedEventIds}
            onChange={handleEventChange}
            mode='CheckBox'
            showSelectAll={true}
            selectAllText='Select All'
            unSelectAllText='Clear'
          >
            <Inject services={[CheckBoxSelection]} />
          </MultiSelectComponent>
        </div>

        <div className='cloud-scheduler-input'>
          <MultiSelectComponent
            placeholder='Plan'
            value={selectedPlanIds}
            dataSource={allAvailablePlans}
            fields={{ text: 'plan_name', value: 'ID' }}
            onChange={handlePlanChange}
            mode='CheckBox'
            showSelectAll={true}
            selectAllText='Select All'
            unSelectAllText='Clear'
          >
            <Inject services={[CheckBoxSelection]} />
          </MultiSelectComponent>
        </div>

        <ButtonComponent
          style={{ fontSize: '12px' }}
          onClick={() => {
            const currentPlanIds = selectedPlanIdsRef.current;
            const primaryPlanId = currentPlanIds.length > 0 ? currentPlanIds[0] : 0;

            dispatch(
              setIsPlanSelected({
                isOrderPlanSelected: true,
                isOpPlanSelected: true,
                planId: primaryPlanId,
                planIds: currentPlanIds,
              })
            );

            dispatch(setIsAppliedEventPlan({ isAppliedEventPlan: true }));
          }}
          content='Apply'
        />
      </div>
      <TheTabComponent />
    </div>
  );
};

export default memo(CloudPlanner);
